/**
 * Frontend feature-flag helpers.
 *
 * Single source of truth for client-side feature toggles. Keep this
 * file small — flags should only live here if at least two call sites
 * need them, or if the flag governs a route guard plus a link
 * affordance (the legacy-progress sunset is the latter case).
 *
 * Rules of engagement:
 *   - Read `process.env.REACT_APP_*` only inside this file. Components
 *     import the boolean — never the raw env string.
 *   - Default to the SAFE/CURRENT behaviour when the env var is unset
 *     or unrecognised. Flags should be opt-IN to the new behaviour or
 *     opt-OUT of the legacy behaviour, never an unintended kill switch.
 *   - When the flag's job is done, delete it here AND remove the
 *     downstream call sites — don't leave dead toggles.
 */

/**
 * /learn/progress-legacy sunset flag (§23 Step 3, May 2026).
 *
 * The 1255-line `Dashboard.js` is preserved at /learn/progress-legacy
 * as an escape hatch for any pupil who still wants the granular
 * module/streak/lesson-count view that pre-dates the calm
 * test-readiness home at /learn/progress.
 *
 * Default: ENABLED (legacy route + escape-hatch link both visible).
 * Set REACT_APP_ENABLE_LEGACY_PROGRESS=0 to:
 *   - hide the "Detailed lesson dashboard" link on /learn/progress
 *   - redirect /learn/progress-legacy → /learn/progress
 *
 * Implementation sequence per §23:
 *   - Step 3 (now): flag added, default = ENABLED. Zero pupil change.
 *   - Step 16 (after aggregator home ships): flip default to DISABLED
 *     in prod. Remove link 14 days later. Delete Dashboard.js 30 days
 *     after that.
 *
 * Any value other than the exact string "0" is treated as ENABLED to
 * fail safe — a misconfigured env should NOT remove the escape hatch.
 */
export const LEGACY_PROGRESS_ENABLED =
  (process.env.REACT_APP_ENABLE_LEGACY_PROGRESS ?? "1").trim() !== "0";
